import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePageTitle } from '../../context/PageTitleContext';
import { useSidebar } from '../../context/SidebarContext';
import { advancedReportService } from '../../services/api';
import { Card, Table, PageHeader, Pagination } from '../common';
import { default as usePageState } from '../../hooks/usePageState';
import { downloadExcel } from '../../utils/exportUtils';
import { useAuth } from '../../context/AuthContext';
import useDepartment from '../../hooks/useDepartment';

const ItTeamReport = () => {
  const navigate = useNavigate();
  const { setPageTitle, setPageSubtitle } = usePageTitle();
  const { isCollapsed } = useSidebar();
  const { getPagePermissions } = useAuth();
  const dept = useDepartment();
  // Category Type is IT's Software/Hardware split; BioMedical has no such
  // division, so the column and its filter are omitted there.
  const showCategoryType = dept.usesCategoryType;

  // The same screen is registered under a different route per department
  // (IT /reports/it-team, BioMedical /reports/team). Reading IT's route for a
  // BioMedical user found no permissions and hid the Download button.
  const permissions = getPagePermissions(dept.isBioMedical ? '/reports/team' : '/reports/it-team');
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [filterOptions, setFilterOptions] = useState({ regions: [] });

  const [filters, setFilters] = usePageState('itTeamReport', {
    start_date: '',
    end_date: '',
    region_code: '',
    category_type: '',
    search: '',
    currentPage: 1,
    itemsPerPage: 30,
  }, ['/reports']);

  useEffect(() => {
    setPageTitle(`${dept.name} Team Performance`);
    setPageSubtitle('Individual agent performance analysis');
    fetchFilterOptions();
  }, [setPageTitle, setPageSubtitle, dept.name]);

  const fetchFilterOptions = async () => {
    try {
      const response = await advancedReportService.getFilters();
      setFilterOptions(response.data);
    } catch (error) {
      console.error('Error fetching filter options:', error);
    }
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (filters.start_date) params.filter = 'custom';
      if (filters.start_date) params.start_date = filters.start_date;
      if (filters.end_date) params.end_date = filters.end_date;
      if (filters.region_code) params.region_code = filters.region_code;
      if (filters.category_type) params.category_type = filters.category_type;
      const response = await advancedReportService.getItTeamReport(params);
      setData(response.data?.data || []);
    } catch (error) {
      console.error('Error fetching IT team report:', error);
    } finally {
      setLoading(false);
    }
  }, [filters.start_date, filters.end_date, filters.region_code, filters.category_type]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const formatHours = (hours) => {
    if (!hours || hours === 0) return '-';
    if (hours < 1) return `${Math.round(hours * 60)}m`;
    return `${hours}h`;
  };

  const getStatusBadge = (label) => {
    const bgMap = {
      'Excellent': 'bg-green-100 text-green-800',
      'Average': 'bg-yellow-100 text-yellow-800',
      'Poor': 'bg-red-100 text-red-800',
    };
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${bgMap[label] || 'bg-gray-100 text-gray-800'}`}>
        {label}
      </span>
    );
  };

  // Client-side search filter
  const filteredData = useMemo(() => {
    if (!filters.search) return data;
    const term = filters.search.toLowerCase();
    return data.filter(row => row.name?.toLowerCase().includes(term));
  }, [data, filters.search]);

  // Pagination
  const totalItems = filteredData.length;
  const totalPages = Math.ceil(totalItems / filters.itemsPerPage);
  const paginatedData = useMemo(() => {
    const start = (filters.currentPage - 1) * filters.itemsPerPage;
    return filteredData.slice(start, start + filters.itemsPerPage);
  }, [filteredData, filters.currentPage, filters.itemsPerPage]);

  const handleDownload = () => {
    downloadExcel(filteredData, columns, `${dept.code.toLowerCase()}_team_report`);
  };

  const handleDrillDown = async (userId, type, categoryType) => {
    try {
      const params = { user_id: userId, type };
      if (filters.start_date) { params.filter = 'custom'; params.start_date = filters.start_date; }
      if (filters.end_date) params.end_date = filters.end_date;
      if (filters.region_code) params.region_code = filters.region_code;
      if (categoryType) params.category_type = categoryType;
      const response = await advancedReportService.getItTeamTickets(params);
      const ticketIds = response.data?.ticketIds || [];
      if (ticketIds.length > 0) {
        navigate(`/tickets?ticket_ids=${ticketIds.join(',')}`);
      }
    } catch (error) {
      console.error('Error fetching drill-down tickets:', error);
    }
  };

  const columns = [
    {
      header: 'S.No',
      accessor: 'serial',
      width: '50px',
      sticky: true,
      render: (_, __, index) => (filters.currentPage - 1) * filters.itemsPerPage + index + 1
    },
    {
      header: 'Engineer',
      accessor: 'name',
      sticky: true,
      render: (value) => <span className="font-semibold text-gray-900 whitespace-nowrap">{value}</span>
    },
    ...(showCategoryType ? [{
      header: 'Category Type',
      accessor: 'categoryType',
      align: 'center',
      render: (value) => (
        <span className={`px-2 py-1 text-xs font-semibold rounded-full whitespace-nowrap ${
          value === 'Software'
            ? 'bg-blue-100 text-blue-700'
            : value === 'Hardware'
            ? 'bg-purple-100 text-purple-700'
            : 'bg-gray-100 text-gray-700'
        }`}>
          {value || 'Uncategorized'}
        </span>
      )
    }] : []),
    {
      header: 'Currently Assigned',
      accessor: 'currentlyAssigned',
      align: 'center',
      render: (value, row) => (
        <span
          className={`font-semibold ${value > 0 ? 'text-blue-600 cursor-pointer hover:underline' : 'text-gray-500'}`}
          onClick={() => value > 0 && handleDrillDown(row.userId, 'currently_assigned', row.categoryType)}
        >{value}</span>
      )
    },
    {
      header: 'Total Handled',
      accessor: 'totalHandled',
      align: 'center',
      render: (value, row) => (
        <span
          className={`font-semibold ${value > 0 ? 'text-blue-600 cursor-pointer hover:underline' : 'text-gray-500'}`}
          onClick={() => value > 0 && handleDrillDown(row.userId, 'total_handled', row.categoryType)}
        >{value}</span>
      )
    },
    {
      header: 'Escalated',
      accessor: 'escalated',
      align: 'center',
      render: (value, row) => (
        <span
          className={`font-semibold ${value > 0 ? 'text-red-600 cursor-pointer hover:underline' : 'text-green-600'}`}
          onClick={() => value > 0 && handleDrillDown(row.userId, 'escalated', row.categoryType)}
        >{value}</span>
      )
    },
    {
      header: 'Esc. & Reassigned',
      accessor: 'escalatedReassigned',
      align: 'center',
      render: (value, row) => (
        <span
          className={`font-medium ${value > 0 ? 'text-orange-600 cursor-pointer hover:underline' : 'text-gray-500'}`}
          onClick={() => value > 0 && handleDrillDown(row.userId, 'escalated_away', row.categoryType)}
        >{value}</span>
      )
    },
    {
      header: 'Reopen & Reassigned',
      accessor: 'reopenedReassigned',
      align: 'center',
      render: (value) => (
        <span className={`font-medium ${value > 0 ? 'text-orange-600' : 'text-gray-500'}`}>{value}</span>
      )
    },
    {
      header: 'Active',
      accessor: 'active',
      align: 'center',
      render: (value, row) => (
        <span
          className={`font-semibold ${value > 0 ? 'text-blue-600 cursor-pointer hover:underline' : 'text-gray-500'}`}
          onClick={() => value > 0 && handleDrillDown(row.userId, 'active', row.categoryType)}
        >{value}</span>
      )
    },
    {
      header: 'Resolved',
      accessor: 'resolved',
      align: 'center',
      render: (value, row) => (
        <span
          className={`font-semibold ${value > 0 ? 'text-green-600 cursor-pointer hover:underline' : 'text-gray-500'}`}
          onClick={() => value > 0 && handleDrillDown(row.userId, 'resolved', row.categoryType)}
        >{value}</span>
      )
    },
    {
      header: 'Avg Resolution',
      accessor: 'avgResolutionTime',
      align: 'center',
      render: (value) => <span className="text-gray-700">{formatHours(value)}</span>
    },
    {
      header: 'SLA Rate',
      accessor: 'slaRate',
      align: 'center',
      render: (value) => (
        <div className="flex items-center justify-center gap-2">
          <div className="flex-1 bg-gray-200 rounded-full h-2 max-w-[60px]">
            <div
              className="h-2 rounded-full transition-all duration-500"
              style={{
                width: `${Math.min(value, 100)}%`,
                backgroundColor: value >= 80 ? '#10b981' : value >= 50 ? '#f59e0b' : '#ef4444'
              }}
            />
          </div>
          <span className="font-medium text-sm" style={{ color: value >= 80 ? '#059669' : value >= 50 ? '#d97706' : '#dc2626' }}>
            {value}%
          </span>
        </div>
      )
    },
    {
      header: 'Escalation Rate',
      accessor: 'escalationRate',
      align: 'center',
      render: (value) => (
        <span className={`font-medium ${value > 10 ? 'text-red-600' : value > 5 ? 'text-yellow-600' : 'text-green-600'}`}>
          {value}%
        </span>
      )
    },
    {
      header: 'Reopen Rate',
      accessor: 'reopenRate',
      align: 'center',
      render: (value) => (
        <span className={`font-medium ${value > 10 ? 'text-red-600' : value > 5 ? 'text-yellow-600' : 'text-green-600'}`}>
          {value}%
        </span>
      )
    },
    {
      header: 'Overall Score',
      accessor: 'overallScore',
      align: 'center',
      render: (value) => (
        <span className="text-lg font-bold" style={{ color: value >= 75 ? '#059669' : value >= 50 ? '#d97706' : '#dc2626' }}>
          {value}%
        </span>
      )
    },
    {
      header: 'Status',
      accessor: 'statusLabel',
      align: 'center',
      sticky: 'right',
      render: (value) => getStatusBadge(value)
    },
  ];

  return (
    <div className="admin-panel h-full">
      <Card compact={isCollapsed} allowOverflow={true} noBottomPadding={true} className="h-full flex flex-col">
        <PageHeader
          filters={{
            search: {
              value: filters.search,
              onChange: (e) => setFilters({ ...filters, search: e.target.value, currentPage: 1 }),
              placeholder: 'Search engineer...',
            },
            dateRange: {
              startDate: filters.start_date,
              endDate: filters.end_date,
              onStartDateChange: (e) => setFilters({ ...filters, start_date: e.target.value, currentPage: 1 }),
              onEndDateChange: (e) => setFilters({ ...filters, end_date: e.target.value, currentPage: 1 }),
            },
            dropdowns: [
              {
                value: filters.region_code,
                onChange: (e) => setFilters({ ...filters, region_code: e.target.value, currentPage: 1 }),
                options: [{ value: '', label: `All ${dept.regions}` }, ...(filterOptions.regions || [])],
                placeholder: `All ${dept.regions}`,
                searchable: true,
                width: '180px'
              },
              ...(showCategoryType ? [{
                value: filters.category_type,
                onChange: (e) => setFilters({ ...filters, category_type: e.target.value, currentPage: 1 }),
                options: [{ value: '', label: 'All Types' }, ...(filterOptions.categoryTypes || [])],
                placeholder: 'All Types',
                searchable: false,
                width: '160px'
              }] : [])
            ]
          }}
          downloadButton={permissions.can_download ? {
            label: 'Download',
            onClick: handleDownload,
            variant: 'outline',
            className: 'border-primary-600 text-primary-600 hover:bg-primary-50 focus:ring-primary-500',
            disabled: loading || filteredData.length === 0
          } : null}
        />
        <div className="mb-2 px-1">
          <div className="flex flex-wrap gap-2 mb-2">
            <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">Excellent: Score &ge; 75%</span>
            <span className="px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">Average: Score &ge; 50%</span>
            <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">Poor: Score &lt; 50%</span>
          </div>
          <div className="bg-blue-50 border-l-4 border-blue-400 p-2 rounded">
            <p className="text-xs text-blue-800">
              <strong>Currently Assigned</strong> = Tickets currently with the engineer.
              <strong> Total Handled</strong> = Currently assigned + Escalated away + Reopened away.
              <strong>Active</strong> = Tickets in Open, In Progress, and Reopened status.
            {/* </p>
            <p className="text-xs text-blue-800 mt-1"> */}
              <strong>SLA Rate</strong> = Resolved within SLA without escalations / Currently Assigned.
              <strong> Escalation Rate</strong> = Weighted by severity (Level 1=1pt, Level 2-3=2pts, Level 4+=3pts).
            {/* </p>
            <p className="text-xs text-blue-800 mt-1"> */}
              <strong>Resolved</strong> = Tickets closed by engineer (from history).
              <strong> Reopen Rate</strong> = Tickets reopened after engineer closed them / Resolved.
            {/* </p>
            <p className="text-xs text-blue-800 mt-1"> */}
              <strong>Overall Score</strong> = SLA Rate (40%) + (100 - Escalation Rate) (30%) + (100 - Reopen Rate) (30%).
            </p>
          </div>
        </div>
        <Table
          columns={columns}
          data={paginatedData}
          loading={loading}
          emptyMessage={`No ${dept.name} team performance data available for the selected filters`}
        />
        <Pagination
          currentPage={filters.currentPage}
          totalPages={totalPages}
          totalItems={totalItems}
          itemsPerPage={filters.itemsPerPage}
          onPageChange={(page) => setFilters({ ...filters, currentPage: page })}
          onItemsPerPageChange={(count) => setFilters({ ...filters, itemsPerPage: count, currentPage: 1 })}
        />
      </Card>
    </div>
  );
};

export default ItTeamReport;
